import { useQuery, useMutation } from '@tanstack/react-query'
import { LocalesFilterParams, CreateLocaleParams, UpdateLocaleParams } from '@/types/locale'
import { useLocalesStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useLocales = (params: LocalesFilterParams) => {
  const fetchLocales = useLocalesStore(state => state.fetchLocales)

  return useQuery({
    queryKey: ['locales', params],
    queryFn: () => fetchLocales(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useLocale = (id: string) => {
  const getLocale = useLocalesStore(state => state.getLocale)

  return useQuery({
    queryKey: ['locale', id],
    queryFn: () => getLocale(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateLocale = () => {
  const createLocale = useLocalesStore(state => state.createLocale)

  return useMutation({
    mutationFn: (params: CreateLocaleParams) => createLocale(params),
    onSuccess: () => {}
  })
}

export const useUpdateLocale = () => {
  const updateLocale = useLocalesStore(state => state.updateLocale)

  return useMutation({
    mutationFn: ({ localeId, params }: { localeId: string; params: UpdateLocaleParams }) =>
      updateLocale(localeId, params),
    onSuccess: () => {}
  })
}

export const useSortLocale = () => {
  const sortLocale = useLocalesStore(state => state.sortLocale)

  return useMutation({
    mutationFn: ({ localeId, type }: { localeId: string; type: SortOrderEnum }) => sortLocale(localeId, type),
    onSuccess: () => {}
  })
}

export const useDeleteLocale = () => {
  const deleteLocale = useLocalesStore(state => state.deleteLocale)

  return useMutation({
    mutationFn: (id: string) => deleteLocale(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteLocales = () => {
  const bulkDeleteLocales = useLocalesStore(state => state.bulkDeleteLocales)
  const deselectAllLocales = useLocalesStore(state => state.deselectAllLocales)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteLocales(ids),
    onSuccess: response => {
      deselectAllLocales()
      handleBulkDeleteSuccess(response)
    }
  })
}
